import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Search } from "lucide-react";

import { useCoachRail } from "@/context/CoachRailContext";
import { cn } from "@/lib/utils";

const PEOPLE_PATH = "/people";

/**
 * The header search from the platform sketch: typing filters the people list
 * live (the field only writes `/people?q=`; the list does the filtering),
 * pointer focus or Enter opens the list, and ⌘/Ctrl+Enter hands the text to
 * Anser instead. ⌘K keeps opening the command palette.
 */
export function ShellSearch({ className }: { className?: string }) {
  const { t } = useTranslation("nav");
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [params] = useSearchParams();
  const { openWith } = useCoachRail();
  const onPeople = pathname === PEOPLE_PATH;
  const urlQuery = onPeople ? (params.get("q") ?? "") : "";
  const [value, setValue] = useState(urlQuery);
  const pointerFocus = useRef(false);

  // The URL is the source of truth while on the list (the list's own field
  // writes it too); elsewhere the box starts empty.
  useEffect(() => {
    setValue(urlQuery);
  }, [urlQuery]);

  const go = (q: string, replace: boolean) => {
    const search = q ? `?q=${encodeURIComponent(q)}` : "";
    navigate({ pathname: PEOPLE_PATH, search }, { replace });
  };

  return (
    <div
      className={cn(
        "flex h-[42px] min-w-0 flex-1 items-center gap-2.5 rounded-g-md bg-g-bg-input px-4 text-g-ink-400",
        "focus-within:outline focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-g-ring",
        className,
      )}
    >
      <Search className="size-[17px] shrink-0" aria-hidden="true" />
      <input
        type="search"
        value={value}
        aria-label={t("search.label")}
        placeholder={t("search.placeholder")}
        title={t("search.askHint")}
        className="min-w-0 flex-1 truncate bg-transparent text-[14px] text-g-ink-900 outline-none placeholder:text-g-ink-400"
        onPointerDown={() => {
          pointerFocus.current = true;
        }}
        onFocus={() => {
          // Keyboard tabbing through the header must not navigate.
          if (pointerFocus.current && !onPeople) go(value.trim(), false);
          pointerFocus.current = false;
        }}
        onChange={(event) => {
          const next = event.target.value;
          setValue(next);
          go(next.trim(), onPeople);
        }}
        onKeyDown={(event) => {
          if (event.key !== "Enter") return;
          event.preventDefault();
          const q = value.trim();
          if ((event.metaKey || event.ctrlKey) && q) {
            openWith(q);
            return;
          }
          go(q, onPeople);
        }}
      />
    </div>
  );
}
